import { useTranslation } from 'react-i18next';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '@/utils/seo';
import ElegantHeader from '@/components/feature/ElegantHeader';
import ElegantFooter from '@/components/feature/ElegantFooter';
import ElegantHero from '@/components/feature/ElegantHero';
import ElegantSectionTitle from '@/components/feature/ElegantSectionTitle';

export default function StatsPage() {
  const { t, i18n } = useTranslation();

  const getSchemaLang = () => {
    const map: Record<string, string> = { ja: 'ja-JP', en: 'en-US', ko: 'ko-KR', 'zh-TW': 'zh-TW' };
    return map[i18n.language] || 'ja-JP';
  };

  useSEO({
    title: t('seo.statsTitle'),
    description: t('seo.statsDescription'),
    keywords: t('seo.statsKeywords'),
    path: '/stats',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(t('seo.statsTitle'), t('seo.statsDescription'), '/stats', getSchemaLang()),
        generateBreadcrumbSchema([
          { name: t('nav.topPage'), path: '/' },
          { name: t('nav.stats'), path: '/stats' },
        ]),
      ],
    },
  });

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <ElegantHeader />

      <ElegantHero
        title={t('stats.title')}
        subtitle={t('stats.description')}
        tag="Track Records"
        breadcrumb={[
          { label: t('nav.topPage'), to: '/' },
          { label: t('nav.stats') },
        ]}
      />

      {/* Description */}
      <section className="pt-20 md:pt-28 pb-0 px-6 md:px-10 max-w-[900px] mx-auto text-center">
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className="w-16 h-px" style={{ background: 'linear-gradient(90deg, transparent, #C9A227)' }} />
          <i className="ri-bar-chart-2-line text-lg" style={{ color: '#C9A227' }} />
          <div className="w-16 h-px" style={{ background: 'linear-gradient(90deg, #C9A227, transparent)' }} />
        </div>
        <p
          className="leading-relaxed tracking-wide"
          style={{
            fontFamily: "'Noto Serif JP', serif",
            color: '#3E3B34',
            fontSize: 'clamp(14px, 1.5vw, 18px)',
          }}
        >
          {t('stats.description')}
        </p>
      </section>

      {/* Client Categories */}
      <main className="py-20 md:py-28 px-6 md:px-10 max-w-[1100px] mx-auto">
        <div className="mb-16">
          <ElegantSectionTitle
            tag="Clients"
            title={t('stats.clientCategoriesTitle')}
          />
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          {(t('stats.clientCategories', { returnObjects: true }) as string[]).map((cat: string) => (
            <span
              key={cat}
              className="inline-block py-2.5 text-sm tracking-wider cursor-default transition-all duration-300 text-center"
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontWeight: 500,
                color: '#1A1A1A',
                background: '#FFFFFF',
                border: '1px solid rgba(201, 162, 39, 0.45)',
                letterSpacing: '0.08em',
                fontSize: '0.875rem',
                width: '160px',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLSpanElement).style.background = '#C9A227';
                (e.currentTarget as HTMLSpanElement).style.color = '#FFFFFF';
                (e.currentTarget as HTMLSpanElement).style.borderColor = '#C9A227';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLSpanElement).style.background = '#FFFFFF';
                (e.currentTarget as HTMLSpanElement).style.color = '#1A1A1A';
                (e.currentTarget as HTMLSpanElement).style.borderColor = 'rgba(201, 162, 39, 0.45)';
              }}
            >
              {cat}
            </span>
          ))}
        </div>
        <div className="mt-14 flex items-center justify-center gap-4">
          <div className="w-16 h-px" style={{ background: 'linear-gradient(90deg, transparent, #D4CFC4)' }} />
          <div className="w-1.5 h-1.5" style={{ background: '#C9A227', transform: 'rotate(45deg)' }} />
          <div className="w-16 h-px" style={{ background: 'linear-gradient(90deg, #D4CFC4, transparent)' }} />
        </div>
      </main>

      <ElegantFooter />
    </div>
  );
}